import { toast } from '@/hooks/use-toast';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { faCamera, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IonActionSheet } from '@ionic/react';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { isPlatform } from '@ionic/react';
import { ImageWithFallback } from './ui/fallbackImage';
import { updateUserAvatar } from '@/utils/imageUpload';

export default function AvatarUpload({ className, initialImage, onAvatarUpdated, size }: { className?: string; initialImage?: string | null; onAvatarUpdated?: (url: string) => void; size?: 'md' | 'lg' }) {

  const { t } = useTranslation();
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [avatar, setAvatar] = useState(initialImage ? initialImage : null);
  const [timestamp, setTimestamp] = useState(Date.now());

  useEffect(() => {
    const getCurrentUser = async () => {
      const result = await FirebaseAuthentication.getCurrentUser();

      if (result && result.user && result.user.photoUrl) {
        setAvatar(result.user.photoUrl);
      }
    };

    if (!initialImage) {
      getCurrentUser();
    }
  }, []);

  const updateProfileImage = async (image: string) => {
    try {
      const downloadURL = await updateUserAvatar(image);
      setAvatar(downloadURL);
      setTimestamp(Date.now());
      onAvatarUpdated?.(downloadURL);
      console.log("downloadurl ", downloadURL);
      toast({
        description: (
          <span>
            <FontAwesomeIcon width={24} height={24} color="green" className="mr-2" icon={faCircleCheck} />
            <span className="text-lg">{t('profile_picture_updated')}</span>
          </span>
        ),
      });
    } catch (error) {
      console.error('Error updating profile image:', error);
      toast({
        description: t('error_updating_profile_picture'),
        variant: 'destructive'
      });
    }
  };

  const takePhoto = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Base64,
        source: CameraSource.Camera
      });

      if (image.base64String) {
        const imageUrl = `data:image/jpeg;base64,${image.base64String}`;
        await updateProfileImage(imageUrl);
      }
    } catch (error) {
      console.error('Error taking photo:', error);
      toast({
        description: t('error_taking_photo'),
        variant: 'destructive'
      });
    }
  };

  const selectPhoto = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: true,
        resultType: CameraResultType.Base64,
        source: CameraSource.Photos
      });

      if (image.base64String) {
        const imageUrl = `data:image/jpeg;base64,${image.base64String}`;
        await updateProfileImage(imageUrl);
      }
    } catch (error) {
      console.error('Error selecting photo:', error);
      Sentry.captureException(error);
      toast({
        description: t('error_selecting_photo'),
        variant: 'destructive'
      });
    }
    finally {
      setShowActionSheet(false);
    }
  };

  const handleFileChange = async (event: any) => {
    try {
      const file = event.target.files[0];
      await updateProfileImage(file);
    } catch (error) {
      console.error('Error handling file change:', error);
      toast({
        description: t('error_handling_file_change'),
        variant: 'destructive'
      });
    }
  };

  const handleAvatarUpload = () => {
    if (isPlatform('mobile')) {
      setShowActionSheet(true);
    } else {
      document.getElementById('fileInput')?.click();
    }
  };

  return (
    <div className="flex items-center space-x-6">
      {/* Avatar Section */}
      <div className="relative">
        <div className="cursor-pointer" onClick={handleAvatarUpload}>
          <div className={`${className} ${size === 'lg' ? 'size-32' : 'size-24'} bg-card overflow-hidden rounded-full`}>
            {avatar ? (
              <ImageWithFallback
                key={timestamp}
                src={avatar ? `${avatar}` : '/assets/images/fallback.jpg'}
                alt=""
                className="rounded-full object-fit"
                fallbackImage='/assets/images/fallback.jpg'
              />
            ) : (
              <div className="flex size-full items-center justify-center bg-gray-300 hover:brightness-75">
                <FontAwesomeIcon icon={faCamera} className="text-muted" />
              </div>
            )}
          </div>
          {/* Camera Icon Button */}
          <Button
            type="button"
            size="icon"
            variant="secondary"
            className="absolute bottom-0 right-0 z-10 hover:bg-secondary rounded-full p-2 shadow-md focus:outline-none"
            onClick={(e) => {
              e.stopPropagation();
              handleAvatarUpload();
            }}
            aria-label="Upload Avatar"
          >
            <FontAwesomeIcon icon={faCamera} />
          </Button>
        </div>
      </div>
      <input type="file" id="fileInput" style={{ display: 'none' }} onChange={handleFileChange} />
      <IonActionSheet
        isOpen={showActionSheet}
        onDidDismiss={() => setShowActionSheet(false)}
        color='transparent'
        className='text-foreground'
        buttons={[
          {
            text: t('take_photo'),
            handler: takePhoto
          },
          {
            text: t('choose_photo'),
            handler: selectPhoto
          },
          {
            text: t('delete'),
            role: 'destructive',
            handler: () => {
              setAvatar('');
            }
          },
          {
            text: t('cancel'),
            role: 'cancel'
          }
        ]}
      />
    </div>
  );
}
